import { useContext, useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { type EntityATI } from '@atlassian/search-client/common/types';
import {
	type TProductDataRegions as ProductDataRegions,
	type TProductEdition as ProductEdition,
} from '@atlassian/search-client/fetch-search-config';
import { type ThirdPartyFilters } from '@atlassian/search-client/query-generator';
import { getBackendExperiment } from '@atlassian/search-experiment/backend';
import {
	type BackendExperiment,
	type ExperimentConfig,
	type FrontendExperiments,
} from '@atlassian/search-experiment/types';

import { ApplicationModes } from '../../../../../common/constants/application-modes';
import type {
	BootstrapState,
	Features,
	OnSearchCompletedCallback,
} from '../../../../../common/constants/bootstrap';
import { TypeFilterValueKey } from '../../../../../common/constants/filters/universal-type';
import type { NounKeys } from '../../../../../common/constants/nouns';
import { type PrimaryProductKey, ProductKeys } from '../../../../../common/constants/products';
import type { BootstrapVariables } from '../../../../../common/constants/relay/types';
import type { ThirdPartyConfigsBootstrap } from '../../../../../common/constants/schemas/3p-config';
import { productKeySchema } from '../../../../../common/constants/schemas/product-key-schema';
import type { SearchQueryStringParams } from '../../../../../common/constants/schemas/query-params';
import type { UserDetailsWithStatus } from '../../../../../common/constants/types';
import { getApplicationMode } from '../../../../../common/utils/get-application-mode';
import { getUser } from '../../../../../common/utils/get-user';
import {
	DynamicBootstrapContext,
	StaticBootstrapContext,
	StaticBootstrapStoreContext,
} from '../index';
import { useStaticBootstrapSelector } from '../static-bootstrap-store';

/**
 * Hi Rovodev
 * Review the below steps and share back a plan before implementing. Think about each step and the impact on code sites, and share any concerns you have about the plan or steps. The goal is to migrate the accessors in a way that minimizes risk and code changes in code sites.
 *
 * To migrate each accessor safely (single PR) without modifying existing code sites:
 * 1. Identify accessor e.g. `useFeatures`
 * 2. In ../../index.tsx
 * 		a. Add "Store" suffix to hook and a deprecated JSDOC, e.g. `useFeatures` -> `useFeaturesStore`. Ensure there is a strict typescript return type
 * 		b. Add a new hook (with the base name) e.g. `useFeatures` that is exported, and with the EXP check (serveBootstrapProviderExp)
 * 			T: use the import from (3) in accessors
 * 			F: use (2a) with the Store suffix
 * 3. In this file ./accessors:
 * 		a. Create context version of hook `useFeaturesContext`. Ensure there is a strict typescript return type
 * 		b. Add test coverage for this hook
 * 		c. (optional) Create a non-exported subfunction if the logic is complex, e.g. see useBootstrapStateContext's handling of callbacks with a merged store
 * 4. in ../../../index.tsx
 *		a. Remove barrel file export to reduce code path ambiguity
 * 5. Fix imports in code sites impacted by (4) including tests/mocking;
 *		This should be the *only* code change required in code sites
 * Now this code path is safe to migrate via FG rollout, with minimal changes in code sites!
 *
 * Note: the barrel file removals are due to eslint-error from @atlaskit/platform/no-relative-barrel-file-imports
 *
 * Later cleanup (single PR):
 * 1. Replace this file into ../index.tsx and remove the Context suffix from each export
 *
 * DO NOT CHANGE ../../accessors/index.tsx
 *
 * At the end of this migration we'll also likely drop the `bootstrap` path segment entirely.
 */

export type HookReturn<T> = T[];

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * This is being actively migrated away from in QS-9194
 *
 * Instead use specific per-value accessors
 *
 * e.g. const [{ useCloudId, useQueryParams }] = useBootstrap()
 * -> const [cloudId] = useCloudId();
 * -> const [queryParams] = useQueryParams();
 */
export const useBootstrapContext = (): HookReturn<BootstrapState> => {
	const staticContext = useContext(StaticBootstrapContext);
	const dynamicContext = useContext(DynamicBootstrapContext);

	// Unused, clean up when removing rovo-search-remove-rss-bootstrap-fg
	// Here due to type strictness on BootstrapState
	const unusedProps = {
		prevQueryParams: {},
		initialSpaceARIs: [],
	};

	return [
		{ ...unusedProps, ...staticContext, ...dynamicContext },
	] satisfies HookReturn<BootstrapState>;
};

/**
 * Retrieve the current application mode from context.
 * We're intentionally not exposing a simple boolean (eg. isRovoEnabled) as that will not scale if more modes are introduced.
 */
export const useApplicationModeContext = (): ApplicationModes =>
	useStaticBootstrapSelector(useContext(StaticBootstrapStoreContext), (ctx) => ctx.applicationMode);

/**
 * Retrieve linked cloud ARIs from context.
 */
export const useLinkedCloudARIsContext = (): string[] | undefined =>
	useStaticBootstrapSelector(useContext(StaticBootstrapStoreContext), (ctx) => ctx.linkedCloudARIs);

/**
 * Retrieve the initial text query param from context.
 */
export const useInitialTextQueryParamContext = (): string | undefined =>
	useStaticBootstrapSelector(
		useContext(StaticBootstrapStoreContext),
		(ctx) => ctx.initialTextQueryParam,
	);

/**
 * Retrieve the referrer from context.
 */
export const useReferrerContext = (): string | undefined => {
	const dynamicContext = useContext(DynamicBootstrapContext);
	return dynamicContext.referrer;
};

/*
 * Retrieve whether filters discoverability 2 is enabled from context.
 * Returns true if primary product is Confluence or Jira.
 */
export const useFiltersDiscoverability2EnabledContext = (): boolean =>
	useStaticBootstrapSelector(useContext(StaticBootstrapStoreContext), (ctx) => {
		const primaryProduct = ctx.primaryProduct;
		if (!primaryProduct) {
			return false;
		}
		return (
			primaryProduct === ProductKeys.Confluence ||
			primaryProduct === ProductKeys.Jira ||
			primaryProduct === ProductKeys.Atlas
		);
	});

/**
 * Retrieve third party configs from context.
 */
export const useThirdPartyConfigsContext = (): ThirdPartyConfigsBootstrap =>
	useStaticBootstrapSelector(
		useContext(StaticBootstrapStoreContext),
		(ctx) => ctx.thirdPartyConfigs,
	);

/**
 * Retrieve product edition from context.
 */
export const useProductEditionContext = (): ProductEdition | undefined =>
	useStaticBootstrapSelector(useContext(StaticBootstrapStoreContext), (ctx) => ctx.productEdition);

/**
 * Retrieve whether Rovo is free from context.
 * Note: this hook is only temporary and should be removed once Rovo is enabled for all users.
 */
export const useIsRovoFreeContext = (): boolean =>
	useStaticBootstrapSelector(
		useContext(StaticBootstrapStoreContext),
		(ctx) => !!ctx.rovoFeatures?.scas,
	);

/**
 * Retrieve the Intercom HMAC from context.
 */
export const useIntercomHmacContext = (): string | undefined =>
	useStaticBootstrapSelector(useContext(StaticBootstrapStoreContext), (ctx) => ctx.intercomHmac);

/**
 * Retrieve the experiment config from context.
 */
export const useExperimentConfigContext = (): ExperimentConfig =>
	useStaticBootstrapSelector(
		useContext(StaticBootstrapStoreContext),
		(ctx) => ctx.experimentConfig,
	);

/**
 * Retrieve whether multi-site search in Rovo is enabled from context.
 */
export const useMultiSiteSearchInRovoContext = (): boolean =>
	useStaticBootstrapSelector(
		useContext(StaticBootstrapStoreContext),
		(ctx) => ctx.multiSiteSearchInRovo,
	);

/**
 * Retrieve whether the current tenant is Hello from context.
 */
export const useIsHelloContext = (): boolean =>
	useStaticBootstrapSelector(useContext(StaticBootstrapStoreContext), (ctx) => ctx.isHello);

/**
 * Retrieve the search session ID from context.
 */
export const useSearchSessionIdContext = (): string => {
	const dynamicContext = useContext(DynamicBootstrapContext);
	return dynamicContext.searchSessionId;
};

/**
 * Retrieve available nouns from context.
 */
export const useAvailableNounsContext = (): NounKeys[] =>
	useStaticBootstrapSelector(useContext(StaticBootstrapStoreContext), (ctx) => ctx.nouns);

/**
 * Retrieve available products from context.
 */
export const useAvailableProductsContext = (): ProductKeys[] =>
	useStaticBootstrapSelector(useContext(StaticBootstrapStoreContext), (ctx) => ctx.products);

/**
 * Retrieve analytics attributes from context.
 */
export const useAnalyticsAttributesContext = (): Record<string, any> | undefined =>
	useStaticBootstrapSelector(
		useContext(StaticBootstrapStoreContext),
		(ctx) => ctx.analyticsAttributes,
	);

/**
 * Retrieve Smart Link usage data from context.
 */
export const useSmartLinkUsageContext = (): Record<string, number> | undefined => {
	const dynamicContext = useContext(DynamicBootstrapContext);
	return dynamicContext.smartLinkUsage;
};

/**
 * Retrieve whether the bootstrap config has finished loading from context.
 */
export const useIsConfigLoadedContext = (): boolean => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.isConfigLoaded;
};

/**
 * Retrieve whether available products have been loaded from context.
 * This is the shared context accessor used by both useIsAvailableProductsLoaded and useBootstrapLoading.
 */
export const useIsAvailableProductsLoadedContext = (): boolean => {
	const staticContext = useContext(StaticBootstrapContext);

	return useMemo(() => {
		return staticContext.products != null && staticContext.products.length > 0;
	}, [staticContext.products]);
};

/**
 * Retrieve frontend experiments from context.
 */
export const useFrontendExperimentsContext = (): FrontendExperiments => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.experimentConfig.frontendExperiments;
};

/**
 * Retrieve the backend experiment from context.
 * Accepts entities and thirdPartyFilters as arguments, matching the store hook's selector signature.
 */
export const useBackendExperimentContext = (args: {
	entities: EntityATI[];
	thirdPartyFilters: ThirdPartyFilters;
}): BackendExperiment => {
	const staticContext = useContext(StaticBootstrapContext);
	const {
		experimentConfig,
		productEdition,
		productDataRegions,
		experience,
		cloudId,
		isJiraIssueIndexed,
		isHello,
	} = staticContext;

	return useMemo(() => {
		return getBackendExperiment({
			experimentConfig: experimentConfig,
			entities: args.entities,
			productEdition: productEdition,
			isHello,
			productDataRegions: productDataRegions,
			experience: experience,
			tenantId: cloudId,
			isJiraIssueIndexed: isJiraIssueIndexed,
			thirdPartyFilters: args.thirdPartyFilters,
		});
	}, [
		experimentConfig,
		productEdition,
		cloudId,
		productDataRegions,
		experience,
		isJiraIssueIndexed,
		args.entities,
		args.thirdPartyFilters,
		isHello,
	]);
};

/**
 * Retrieve the callback to set query params from context. Does not modify the store.
 */
export const useOnSearchCompletedContext = (): OnSearchCompletedCallback => {
	const { onSearchCompleted } = useContext(StaticBootstrapContext);
	return onSearchCompleted;
};

/**
 * Retrieve BootstrapVariables (with applied callback) from context.
 * Merges static and dynamic context, then applies the callback transformation.
 */
const _useBootstrapStateContextWithSelector = (
	cb: (state: BootstrapVariables) => BootstrapVariables,
): BootstrapVariables => {
	const dynamicContext = useContext(DynamicBootstrapContext);
	const store = useContext(StaticBootstrapStoreContext);

	// Select each field individually so Object.is comparison works on primitives/stable refs.
	// This ensures we only re-render when a field useQueryPlan reads actually changes.
	const cloudId = useStaticBootstrapSelector(store, (s) => s.cloudId);
	const primaryProduct = useStaticBootstrapSelector(store, (s) => s.primaryProduct);
	const thirdPartyConfigs = useStaticBootstrapSelector(store, (s) => s.thirdPartyConfigs);
	const experimentConfig = useStaticBootstrapSelector(store, (s) => s.experimentConfig);
	const experimentLayers = useStaticBootstrapSelector(store, (s) => s.experimentLayers);
	const products = useStaticBootstrapSelector(store, (s) => s.products);
	const allFilterExcludedProducts = useStaticBootstrapSelector(
		store,
		(s) => s.allFilterExcludedProducts,
	);
	const productEdition = useStaticBootstrapSelector(store, (s) => s.productEdition);
	const productDataRegions = useStaticBootstrapSelector(store, (s) => s.productDataRegions);
	const isJiraIssueIndexed = useStaticBootstrapSelector(store, (s) => s.isJiraIssueIndexed);
	const isRovoEnabled = useStaticBootstrapSelector(store, (s) => s.isRovoEnabled);
	const user = useStaticBootstrapSelector(store, (s) => s.user);
	const nouns = useStaticBootstrapSelector(store, (s) => s.nouns);
	const overrides = useStaticBootstrapSelector(store, (s) => s.overrides);

	const context = useMemo(() => {
		return cb({
			cloudId,
			primaryProduct,
			thirdPartyConfigs,
			searchSessionId: dynamicContext?.searchSessionId,
			experimentConfig,
			experimentLayers,
			products,
			allFilterExcludedProducts,
			productEdition,
			productDataRegions,
			isJiraIssueIndexed,
			isRovoEnabled,
			user,
			nouns,
			referrer: dynamicContext?.referrer,
			...(fg('enable_rovo_search_overrides') && { overrides }),
		});
	}, [
		cloudId,
		primaryProduct,
		thirdPartyConfigs,
		experimentConfig,
		experimentLayers,
		products,
		allFilterExcludedProducts,
		productEdition,
		productDataRegions,
		isJiraIssueIndexed,
		isRovoEnabled,
		user,
		nouns,
		overrides,
		dynamicContext?.searchSessionId,
		dynamicContext?.referrer,
		cb,
	]);

	return context;
};

export const useBootstrapStateContext: (
	cb: (state: BootstrapVariables) => BootstrapVariables,
) => BootstrapVariables = _useBootstrapStateContextWithSelector;

/**
 * Retrieve features from context.
 */
export const useFeaturesContext = (): Features | undefined =>
	useStaticBootstrapSelector(useContext(StaticBootstrapStoreContext), (ctx) => ctx.features);

/**
 * Retrieve cloudId from context.
 */
export const useCloudIdContext = (): string | undefined =>
	useStaticBootstrapSelector(useContext(StaticBootstrapStoreContext), (ctx) => ctx.cloudId);

/**
 * Retrieve orgId from context.
 */
export const useOrgIdContext = (): string | undefined =>
	useStaticBootstrapSelector(useContext(StaticBootstrapStoreContext), (ctx) => ctx.orgId);

/**
 * Retrieve primaryProduct from context.
 */
export const usePrimaryProductContext = (): PrimaryProductKey | undefined =>
	useStaticBootstrapSelector(useContext(StaticBootstrapStoreContext), (ctx) => ctx.primaryProduct);

/**
 * Retrieve user details from context with authentication status.
 */
export const useInitialSpaceARIsContext = (): string[] => {
	return []; // no longer used, remove when cleaning up fg rovo-search-remove-rss-bootstrap-fg
};

/**
 * Retrieve user details from context with authentication status.
 */
export const useUserContext = (): UserDetailsWithStatus =>
	getUser(useStaticBootstrapSelector(useContext(StaticBootstrapStoreContext), (ctx) => ctx.user));

/**
 * Retrieve the SAIN host name from static context.
 */
export const useSAINHostNameContext = (): string | undefined =>
	useStaticBootstrapSelector(useContext(StaticBootstrapStoreContext), (ctx) => ctx.SAINHostName);

/**
 * Retrieve whether the user has edit or view plan permission from static context.
 */
export const useHasEditOrViewPlanPermissionContext = (): boolean =>
	useStaticBootstrapSelector(
		useContext(StaticBootstrapStoreContext),
		(ctx) => ctx.hasEditOrViewPlanPermission ?? false,
	);

/**
 * Retrieve whether Rovo is enabled from static context.
 */
export const useIsRovoEnabledContext = (): boolean =>
	useStaticBootstrapSelector(
		useContext(StaticBootstrapStoreContext),
		(ctx) => ctx.isRovoEnabled ?? false,
	);

/**
 * Retrieve experiment layers from static context.
 */
export const useExperimentLayersContext = (): string[] =>
	useStaticBootstrapSelector(
		useContext(StaticBootstrapStoreContext),
		(ctx) => ctx.experimentLayers ?? [],
	);

/**
 * Retrieve the current user ID from static context.
 */
export const useCurrentUserIdContext = (): string | null | undefined =>
	useStaticBootstrapSelector(useContext(StaticBootstrapStoreContext), (ctx) => ctx.user?.id);

/**
 * Retrieve whether the primary product should be auto-selected from context.
 * Returns the primary product key if it should be auto-selected, or null otherwise.
 */
export const useAutoSelectPrimaryProductContext = (): ProductKeys | null => {
	const staticContext = useContext(StaticBootstrapContext);
	const dynamicContext = useContext(DynamicBootstrapContext);

	const { products, primaryProduct, isRovoEnabled } = staticContext;
	const { product: currentSelectedProduct, cql: currentCql } = dynamicContext.queryParams;

	const applicationMode = getApplicationMode({ isRovoEnabled });
	const primaryProductToSelect = productKeySchema.safeParse(primaryProduct);

	const shouldAutoSelectPrimaryProduct =
		!currentSelectedProduct &&
		!currentCql &&
		products &&
		products.length > 0 &&
		applicationMode === ApplicationModes.Unified &&
		primaryProductToSelect.success &&
		primaryProductToSelect.data !== ProductKeys.Atlas;

	if (shouldAutoSelectPrimaryProduct) {
		return primaryProductToSelect.data;
	} else {
		return null;
	}
};

/**
 * Retrieve whether the type filter is set to Incident from dynamic context.
 */
export const useIsTypeFilteredToIncidentContext = (): boolean => {
	const dynamicContext = useContext(DynamicBootstrapContext);
	return dynamicContext.queryParams?.type === TypeFilterValueKey.Incident;
};

/**
 * Retrieve query params from dynamic context.
 * QueryParams change as the user interacts with search (input, filters).
 */
export const useQueryParamsContext = (): SearchQueryStringParams => {
	const dynamicContext = useContext(DynamicBootstrapContext);
	return dynamicContext.queryParams;
};

/**
 * Retrieve the aggregator host name from static context.
 */
export const useAggregatorHostNameContext = (): string | undefined =>
	useStaticBootstrapSelector(
		useContext(StaticBootstrapStoreContext),
		(ctx) => ctx.aggregatorHostName,
	);

/**
 * Retrieve the aggregator absolute URL from static context.
 */
export const useAggAbsoluteUrlContext = (): string | undefined =>
	useStaticBootstrapSelector(useContext(StaticBootstrapStoreContext), (ctx) => ctx.aggAbsoluteUrl);

/**
 * Retrieve all filter excluded products from static context.
 */
export const useAllFilterExcludedProductsContext = (): ProductKeys[] =>
	useStaticBootstrapSelector(
		useContext(StaticBootstrapStoreContext),
		(ctx) => ctx.allFilterExcludedProducts,
	);

/**
 * Retrieve product data regions from static context.
 */
export const useProductDataRegionsContext = (): ProductDataRegions | undefined =>
	useStaticBootstrapSelector(
		useContext(StaticBootstrapStoreContext),
		(ctx) => ctx.productDataRegions,
	);

/**
 * Retrieve whether Jira issues are indexed from static context.
 */
export const useIsJiraIssueIndexedContext = (): boolean =>
	useStaticBootstrapSelector(
		useContext(StaticBootstrapStoreContext),
		(ctx) => ctx.isJiraIssueIndexed,
	);
